import { Markdown } from '@/component/Markdown';
import { isMarkdown } from '@/component/Markdown/utils';
import { useTexts } from '@/feature/lang';
import { formatDateAndTime } from '@/util/date';
import ArticleIcon from '@mui/icons-material/Article';
import SamIcon from '@mui/icons-material/AutoAwesome';
import { Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';
import Translate from '../Translate';

const MessageBubbleContainer = styled(Paper, {
  shouldForwardProp: (prop) =>
    !['isUser', 'isReference'].includes(prop as string),
})<{ isUser: boolean; isReference?: boolean }>(({ theme, isUser }) => ({
  maxWidth: '80%',
  width: 'fit-content',
  minWidth: 0,
  marginLeft: isUser ? 'auto' : theme.spacing(2),
  marginRight: isUser ? theme.spacing(2) : 'auto',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5),
  backgroundColor: isUser
    ? theme.palette.background.paper
    : theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  borderRadius: theme.shape.borderRadius * 1.5,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  position: 'relative',
  '&:last-child': {
    marginBottom: 0,
  },
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: isUser ? 'auto' : -11,
    right: isUser ? -11 : 'auto',
    bottom: theme.spacing(0),
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: isUser
      ? theme.palette.background.paper
      : isUser
      ? theme.palette.background.paper
      : theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const MessageHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Timestamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const MessageContent = styled('div')(({ theme }) => ({
  '& > p, & > div, & > span': {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    margin: 0,
    marginBottom: theme.spacing(1),
  },
  '& > p:last-child, & > div:last-child, & > span:last-child': {
    marginBottom: 0,
  },
}));

const PlainTextContent = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  margin: 0,
  marginBottom: theme.spacing(1),
}));

interface MessageBubbleProps {
  isUser: boolean;
  timestamp: Date;
  content: string | ReactNode;
  isReference?: boolean;
  isStreaming?: boolean;
}

export const MessageBubble = ({
  isUser,
  timestamp,
  content,
  isReference,
  isStreaming = false,
}: MessageBubbleProps) => {
  const texts = useTexts().component.chatbot.message;

  const isStringContent = typeof content === 'string';
  const shouldRenderAsMarkdown = useMemo(() => {
    if (!isStringContent) {
      return false;
    }

    if (isStreaming && (content as string).length > 0) {
      const hasMarkdownPatterns = isMarkdown(content as string);
      return hasMarkdownPatterns || (content as string).length > 20;
    }

    return isMarkdown(content as string);
  }, [isStringContent, content, isStreaming]);

  return (
    <MessageBubbleContainer isUser={isUser} isReference={isReference}>
      <MessageHeader>
        <HeaderText>
          {!isUser ? (
            <>
              {isReference ? (
                <ArticleIcon sx={{ fontSize: 16 }} />
              ) : (
                <SamIcon sx={{ fontSize: 16 }} />
              )}
              {isReference ? (
                <Translate text={texts.referenceTitle} />
              ) : (
                <Translate text={texts.chatbotTitle} />
              )}
            </>
          ) : (
            <Translate text={texts.userTitle} />
          )}
        </HeaderText>
        <Timestamp>{formatDateAndTime(timestamp)}</Timestamp>
      </MessageHeader>
      <MessageContent>
        {isStringContent ? (
          shouldRenderAsMarkdown ? (
            <Markdown>{content as string}</Markdown>
          ) : (
            <PlainTextContent variant="body2">{content}</PlainTextContent>
          )
        ) : (
          content
        )}
      </MessageContent>
    </MessageBubbleContainer>
  );
};
